import { apiClient } from './axios';

import type {
  DealerKitRegistration,
  CreateDealerKitRegistrationDto,
  UpdateDealerKitRegistrationDto,
  PaginatedDealerKitWebhookLogs,
  PaginatedDealerKitContacts,
} from '@repo/shared-types';

export const dealerKitApi = {
  getRegistrations: async () => {
    const response = await apiClient.get<DealerKitRegistration[]>('/dealer-kit/registrations');
    return response.data;
  },

  createRegistration: async (data: CreateDealerKitRegistrationDto) => {
    const response = await apiClient.post<DealerKitRegistration>('/dealer-kit/registrations', data);
    return response.data;
  },

  updateRegistration: async (id: string, data: UpdateDealerKitRegistrationDto) => {
    const response = await apiClient.patch<DealerKitRegistration>(
      `/dealer-kit/registrations/${id}`,
      data,
    );
    return response.data;
  },

  deleteRegistration: async (id: string) => {
    await apiClient.delete(`/dealer-kit/registrations/${id}`);
  },

  getWebhookLogs: async (registrationId: string, page = 1, limit = 20) => {
    const response = await apiClient.get<PaginatedDealerKitWebhookLogs>(
      `/dealer-kit/registrations/${registrationId}/logs`,
      { params: { page, limit } },
    );
    return response.data;
  },

  getContacts: async (page = 1, limit = 20, search?: string) => {
    const response = await apiClient.get<PaginatedDealerKitContacts>('/dealer-kit/contacts', {
      params: { page, limit, search },
    });
    return response.data;
  },

  deleteContact: async (id: string) => {
    await apiClient.delete(`/dealer-kit/contacts/${id}`);
  },

  syncStock: async () => {
    const response = await apiClient.post<DealerKitStockSyncResult>('/dealer-kit/stock/sync');
    return response.data;
  },

  getStockSyncState: async () => {
    const response = await apiClient.get<DealerKitStockSyncState | null>(
      '/dealer-kit/stock/sync-state',
    );
    return response.data;
  },

  getStockTokens: async () => {
    const response = await apiClient.get<StockApiToken[]>('/dealer-kit/stock/tokens');
    return response.data;
  },

  createStockToken: async (name: string) => {
    const response = await apiClient.post<{ token: string; id: string }>(
      '/dealer-kit/stock/tokens',
      { name },
    );
    return response.data;
  },

  revokeStockToken: async (id: string) => {
    await apiClient.delete(`/dealer-kit/stock/tokens/${id}`);
  },

  searchStock: async (params: {
    query?: string;
    filters?: Record<string, unknown>;
    sort?: string;
    page?: number;
    limit?: number;
  }) => {
    const response = await apiClient.post<StockSearchResult>(
      '/dealer-kit/stock/search/test',
      params,
    );
    return response.data;
  },

  backfillSearchText: async () => {
    const response = await apiClient.post<{ updated: number }>('/dealer-kit/stock/search/backfill');
    return response.data;
  },

  getStockListings: async (page = 1, limit = 20, search?: string) => {
    const response = await apiClient.get<PaginatedDealerKitStockListings>(
      '/dealer-kit/stock/listings',
      { params: { page, limit, search } },
    );
    return response.data;
  },

  uploadStockCsv: async (rows: CsvStockRow[]) => {
    const response = await apiClient.post<{ imported: number; errors: string[] }>(
      '/dealer-kit/stock/upload-csv',
      { rows },
    );
    return response.data;
  },

  batchDeleteStock: async (ids: string[]) => {
    const response = await apiClient.post<{ deleted: number }>('/dealer-kit/stock/batch-delete', {
      ids,
    });
    return response.data;
  },

  deleteAllStock: async () => {
    await apiClient.delete('/dealer-kit/stock/all');
  },
};

export interface CsvStockRow {
  stock_listing_id?: string;
  registration?: string;
  vin?: string;
  manufacturer?: string;
  model?: string;
  derivative?: string;
  colour?: string;
  year?: string;
  mileage?: string;
  price?: string;
  fuel_type?: string;
  body_type?: string;
  transmission?: string;
  status?: string;
  doors?: string;
  seats?: string;
  condition?: string;
  notes?: string;
  /** Any additional CSV column (e.g. lot) — stored as a custom field and searchable. */
  [key: string]: string | undefined;
}

export interface StockApiToken {
  id: string;
  name: string;
  token_prefix: string;
  is_active: boolean;
  last_used_at: string | null;
  created_at: string;
  expires_at: string | null;
}

export interface StockSearchHit {
  id: string;
  stock_listing_id: string;
  manufacturer: string;
  model: string;
  trim: string | null;
  colour: string | null;
  year: number | null;
  mileage: number | null;
  price: number | null;
  monthly_price: number | null;
  fuel_type: string | null;
  body_type: string | null;
  transmission: string | null;
  registration: string | null;
  doors: number | null;
  seats: number | null;
  owners: number | null;
  status: string | null;
  location: string | null;
  location_phone: string | null;
  images: string[];
  website_url: string | null;
  derivative: string | null;
  engine_size: string | null;
  bhp: number | null;
  condition: string | null;
  drive_train: string | null;
  vat_status: string | null;
  finance_apr: number | null;
  finance_term: number | null;
  finance_deposit: number | null;
  notes: string | null;
  relevance?: number;
  /** Custom CSV fields (e.g. lot_number) are flattened onto the hit at the top level. */
  [key: string]: unknown;
}

export interface StockSearchResult {
  vehicles: StockSearchHit[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  filters_applied: Record<string, unknown>;
}

export interface DealerKitStockListingRow {
  id: string;
  stock_listing_id: string;
  registration: string | null;
  vin: string | null;
  dealer_id: number | null;
  status: string | null;
  data: Record<string, unknown> | null;
  synced_at: string;
  created_at: string;
  updated_at: string;
}

export interface PaginatedDealerKitStockListings {
  data: DealerKitStockListingRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface DealerKitStockSyncResult {
  started: boolean;
  message: string;
}

export interface DealerKitStockSyncState {
  id: number | null;
  last_synced_at: string | null;
  last_status: string | null;
  last_count: number | null;
  updated_at: string | null;
  running: boolean;
}
